import React, { useState } from "react";
import { Box, Button, Typography } from '@mui/material';
import { useMutation } from "@apollo/client";
import { SEND_EMAIL_MUTATION } from "../../graphql/mutations";
import SmsConsentCheckbox, { SMS_CONSENT_TEXT } from "../../components/legal/SmsConsentCheckbox";

/**
 * Timely365 contact form.
 * - Optional business details included in the email if provided.
 * - Routes to different inboxes based on reason (fallbacks to support).
 *
 * Props:
 * - onClose (required): callback when form is closed elsewhere (not used here but preserved)
 */

const REASONS = [
  { value: "privacy", label: "Privacy / Data Deletion", to: "privacy@timely365.com" },
  { value: "issue", label: "Site / Technical Issue", to: "support@timely365.com" },
  { value: "question", label: "General Question", to: "support@timely365.com" },
  { value: "billing", label: "Billing / Subscription", to: "support@timely365.com" },
  { value: "training", label: "Training / Onboarding", to: "support@timely365.com" },
  { value: "feature", label: "Feature Request", to: "support@timely365.com" },
  { value: "data_correction", label: "Data Correction (Address/Phone/Hours)", to: "support@timely365.com" },
  { value: "partnership", label: "Partnership / Sales", to: "info@timely365.com" },
  { value: "demo", label: "Request a Demo", to: "support@timely365.com" },
];

const getRecipientForReason = (reason) =>
  REASONS.find((r) => r.value === reason)?.to || "support@timely365.com";

const ContactUsForm = ({
  showHeading = true,
  onClose,
  initialName = "",
  initialFacility = "",
  initialEmail = "",
  initialPhone = "",
  initialReason = "question",
  initialSubject = "",
  initialMessage = "",
}) => {
  const [sendEmail] = useMutation(SEND_EMAIL_MUTATION);

  // Form state
  const [name, setName] = useState(initialName);
  const [facility, setFacility] = useState(initialFacility);      // optional business name
  const [email, setEmail] = useState(initialEmail);
  const [phone, setPhone] = useState(initialPhone);            // optional contact number
  const [reason, setReason] = useState(initialReason);
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState(initialMessage);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [smsConsent, setSmsConsent] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!subject.trim()) newErrors.subject = "Subject is required";
    if (!message.trim()) newErrors.message = "Message is required";
    if (!smsConsent) newErrors.smsConsent = "SMS consent is required before submitting this form.";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validate();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    const reasonMeta = REASONS.find((r) => r.value === reason);
    const to = getRecipientForReason(reason);
    const reasonLabel = reasonMeta ? reasonMeta.label : "General";

    // Build bodies
    const textBody =
      `Hello,

You have received a new enquiry from ${name}.
Reason: ${reasonLabel}
Subject: ${subject}
${facility ? `Business: ${facility}\n` : ""}${phone ? `Phone: ${phone}\n` : ""}Email: ${email}
SMS consent: ${smsConsent ? "Yes" : "No"}
SMS consent text: ${SMS_CONSENT_TEXT}

Message:
${message}

Best regards,
${name}`;

    const htmlBody = `
      <p>Hello,</p>
      <p>You have received a new enquiry from <strong>${escapeHtml(name)}</strong>.</p>
      <p><strong>Reason:</strong> ${escapeHtml(reasonLabel)}</p>
      <p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
      ${facility
        ? `<p><strong>Business:</strong> ${escapeHtml(facility)}</p>`
        : ""
      }
      ${phone ? `<p><strong>Phone:</strong> ${escapeHtml(phone)}</p>` : ""}
      <p><strong>Email:</strong> ${escapeHtml(email)}</p>
      <p><strong>SMS consent:</strong> ${smsConsent ? "Yes" : "No"}</p>
      <p><strong>SMS consent text:</strong> ${escapeHtml(SMS_CONSENT_TEXT)}</p>
      <p><strong>Message:</strong><br/>${escapeHtml(message).replace(/\n/g, "<br/>")}</p>
      <p>Best regards,<br/>${escapeHtml(name)}</p>
    `;

    try {
      await sendEmail({
        variables: {
          from_email: email,
          to,
          subject: `[Timely365] ${reasonLabel} - ${subject}`,
          text: textBody,
          html: htmlBody,
        },
      });
      setSubmitted(true);
      setErrors({});
    } catch (error) {
      console.error("Error sending email:", error);
      alert("Sorry, we couldn't send your message. Please try again.");
    }
  };

  if (submitted) {
    return (
      <div style={{ textAlign: "center", padding: "1rem" }}>
        <Typography color="success.main">
          Thank you! We received your request and will get back to you shortly.
        </Typography>
      </div>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', color: 'text.primary',
      colorScheme: theme => theme.palette.mode,
      '& input:not([type="checkbox"]), & select, & textarea': { bgcolor: 'background.default', color: 'text.primary',
        border: '1px solid', borderColor: 'divider', borderRadius: 1, boxSizing: 'border-box',
        font: 'inherit', '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } } }}>
      {showHeading && <h2>Contact Timely365</h2>}

      <form onSubmit={handleSubmit} noValidate>
        {/* Name */}
        <FormRow label="Your Name:" htmlFor="name" error={errors.name}>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.name}
          />
        </FormRow>

        {/* Facility/Business (optional) */}
        <FormRow label="Business (optional):" htmlFor="facility">
          <input
            id="facility"
            type="text"
            value={facility}
            onChange={(e) => setFacility(e.target.value)}
            style={inputStyle}
          />
        </FormRow>

        {/* Email */}
        <FormRow label="Email:" htmlFor="email" error={errors.email}>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.email}
          />
        </FormRow>

        {/* Phone (optional) */}
        <FormRow label="Phone (optional):" htmlFor="phone">
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            style={inputStyle}
          />
        </FormRow>

        {/* Reason */}
        <FormRow label="Reason for Contact:" htmlFor="reason">
          <select
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={inputStyle}
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </FormRow>

        {/* Subject */}
        <FormRow label="Subject:" htmlFor="subject" error={errors.subject}>
          <input
            id="subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            style={inputStyle}
            aria-invalid={!!errors.subject}
          />
        </FormRow>

        {/* Message */}
        <FormRow label="Message:" htmlFor="message" error={errors.message}>
          <textarea
            id="message"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ ...inputStyle, resize: "vertical" }}
            aria-invalid={!!errors.message}
          />
        </FormRow>

        <SmsConsentCheckbox
          checked={smsConsent}
          onChange={setSmsConsent}
          error={errors.smsConsent}
          sx={{ marginBottom: "14px" }}
        />

        <Button type="submit" variant="contained">Submit</Button>
      </form>
    </Box>
  );
};

/* ---------- tiny helpers ---------- */
const inputStyle = { width: "100%", padding: "8px" };

const FormRow = ({ label, htmlFor, error, children }) => (
  <div style={{ marginBottom: "14px" }}>
    <label htmlFor={htmlFor}>{label}</label>
    <br />
    {children}
    {error ? <Typography color="error.main" sx={{ mt: 0.75 }}>{error}</Typography> : null}
  </div>
);

// Basic HTML escaping to avoid broken markup in email
function escapeHtml(s) {
  return String(s)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export default ContactUsForm;

